import {Tag} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';

import {isEmpty, isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  AiComposerLanguageSource,
  AiComposerUnavailableContext,
  AiComposerUsedContext,
} from '../AiComposerDraft';

interface AiComposerContextListProps extends RequiredTestIdProps {
  usedContext: AiComposerUsedContext;
  unavailableContext: AiComposerUnavailableContext;
  languageSource: AiComposerLanguageSource;
  locale: string | Nullish;
  title: string;
}

export function AiComposerContextList(props: AiComposerContextListProps) {
  // The server may answer without a language source; claiming one would be an invented fact.
  const languageSourceLabel =
    props.languageSource === 'customer'
      ? i18n.t('general.labels.customer')
      : i18n.t('general.labels.company');

  return (
    <VStack spacing={2}>
      <Text size="xSmall" color="secondary" overflowWrap="anywhere" alternative>
        {props.title}
      </Text>

      <VStack spacing={1}>
        {props.usedContext.map((fact) => (
          <HStack key={fact.key} spacing={2} align="baseline" wrap>
            <Text size="xSmall" color="secondary" overflowWrap="anywhere" alternative>
              {fact.label}
            </Text>
            <Text
              size="xSmall"
              overflowWrap="anywhere"
              data-testid={suffixTestId(`usedContext-${fact.key}`, props)}
            >
              {fact.value}
            </Text>
          </HStack>
        ))}
      </VStack>

      {/* The facts the record could not provide, so the reader sees why a request went unanswered. */}
      <Show when={!isEmpty(props.unavailableContext)}>
        <VStack spacing={1}>
          <Text size="xSmall" color="secondary" overflowWrap="anywhere" alternative>
            {i18n.t('entity.workshop.labels.notAvailable')}
          </Text>
          <HStack spacing={1} wrap>
            {props.unavailableContext.map((fact) => (
              <Tag
                key={fact.key}
                size="small"
                colorScheme="orange"
                isSubtle
                label={fact.label}
                data-testid={suffixTestId(`unavailableContext-${fact.key}`, props)}
              />
            ))}
          </HStack>
        </VStack>
      </Show>

      <Show when={isNotNil(props.languageSource)}>
        <div
          role="group"
          aria-label={i18n.t('entity.advertisingPlatforms.labels.language')}
          data-testid={suffixTestId('languageSource', props)}
        >
          <VStack spacing={1}>
            <Show when={isNotNil(props.locale)}>
              <HStack spacing={2} align="baseline" wrap>
                <Text size="xSmall" color="tertiary" overflowWrap="anywhere" alternative>
                  {i18n.t('entity.advertisingPlatforms.labels.language')}
                </Text>
                <Text size="xSmall" color="tertiary" overflowWrap="anywhere">
                  {props.locale}
                </Text>
              </HStack>
            </Show>
            <HStack spacing={2} align="baseline" wrap>
              <Text size="xSmall" color="tertiary" overflowWrap="anywhere" alternative>
                {i18n.t('general.labels.source')}
              </Text>
              <Text size="xSmall" color="tertiary" overflowWrap="anywhere">
                {languageSourceLabel}
              </Text>
            </HStack>
          </VStack>
        </div>
      </Show>
    </VStack>
  );
}
